// Timezone utility for industry-grade order management
// Converts timestamps to specified timezone and formats for display

const TZ_OFFSETS = {
  AEST: 10 * 60,   // +10:00
  AEDT: 11 * 60,   // +11:00
  ACST: 9.5 * 60,  // +09:30
  ACDT: 10.5 * 60, // +10:30
  AWST: 8 * 60,    // +08:00
  UTC: 0,           // +00:00
};

/**
 * Convert timestamp to specified timezone
 * @param {string|Date|number} timestamp - ISO string, Date object, or milliseconds
 * @param {string} timezone - Timezone ID (e.g., 'AEST', 'AEDT', 'UTC')
 * @returns {Date} Date object in the specified timezone offset
 */
export function convertToTimezone(timestamp, timezone = 'AEST') {
  const date = new Date(timestamp);
  const offset = TZ_OFFSETS[timezone] || TZ_OFFSETS.AEST;
  const utcTime = date.getTime() + date.getTimezoneOffset() * 60000;
  return new Date(utcTime + offset * 60000);
}

/**
 * Format timestamp as time string in given timezone
 * @param {string|Date|number} timestamp
 * @param {string} timezone
 * @returns {string} e.g., "14:30" or "2:30 PM"
 */
export function formatTimeInTz(timestamp, timezone = 'AEST') {
  const tzDate = convertToTimezone(timestamp, timezone);
  return tzDate.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format timestamp as date+time string in given timezone
 * @param {string|Date|number} timestamp
 * @param {string} timezone
 * @returns {string} e.g., "Mon 25 Jun 14:30"
 */
export function formatDateTimeInTz(timestamp, timezone = 'AEST') {
  const tzDate = convertToTimezone(timestamp, timezone);
  return tzDate.toLocaleString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Get date string in given timezone for grouping
 * @param {string|Date|number} timestamp
 * @param {string} timezone
 * @returns {string} e.g., "2026-06-25"
 */
export function getDateKeyInTz(timestamp, timezone = 'AEST') {
  const tzDate = convertToTimezone(timestamp, timezone);
  const y = tzDate.getFullYear();
  const m = String(tzDate.getMonth() + 1).padStart(2, '0');
  const d = String(tzDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Get timezone label with offset
 * @param {string} timezone
 * @returns {string}
 */
export function getTzLabel(timezone) {
  const offset = TZ_OFFSETS[timezone] || 0;
  const sign = offset >= 0 ? '+' : '';
  const hours = Math.floor(Math.abs(offset) / 60);
  const mins = Math.abs(offset) % 60;
  const tzStr = `${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  return `${timezone} (UTC${tzStr})`;
}
